import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, BookOpen, Compass, MessageCircleQuestion, Users, NotebookText, BookMarked, Languages, ChevronDown } from "lucide-react";
import QuestionCard, { Highlight } from "@/components/QuestionCard";

export const MODES = [
  { key: "all", label: "All" },
  { key: "passage", label: "Passage", icon: BookOpen },
  { key: "questions", label: "Questions", icon: MessageCircleQuestion },
  { key: "people", label: "People", icon: Users },
  { key: "bible", label: "In the Bible", icon: BookOpen },
  { key: "notes", label: "Study notes", icon: NotebookText },
  { key: "commentaries", label: "Commentaries", icon: BookMarked },
];
const COMMENTARY_NAMES = { "matthew-henry": "Matthew Henry", wiersbe: "Wiersbe", gill: "John Gill", jfb: "JFB", spurgeon: "Spurgeon" };

export function ModeChips({ mode, setMode, counts, intent }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="search-mode-chips">
      {MODES.map((m) => {
        const n = m.key === "all" || intent === "passage" ? null : counts[m.key];
        const hidden = m.key === "passage" ? intent !== "passage" : intent === "passage" && m.key !== "all";
        if (hidden) return null;
        const active = mode === m.key;
        return (
          <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`search-mode-${m.key}`} aria-pressed={active}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:border-gold"}`}>
            {m.icon && <m.icon className="h-3.5 w-3.5" />} {m.label}
            {n !== null && n !== undefined && <span className={`font-mono text-[11px] ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{n.toLocaleString()}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function GroupHeading({ icon: Icon, title, count, onMore, testId }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-foreground"><Icon className="h-4 w-4 text-gold" /> {title} <span className="font-mono text-xs text-muted-foreground">{count?.toLocaleString()}</span></h2>
      {onMore && <button type="button" onClick={onMore} data-testid={testId} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">See all <ArrowRight className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

export function useFromSearch() {
  const { pathname, search } = useLocation();
  return { from: `${pathname}${search}` };
}

export function QuestionGrid({ items, terms }) {
  const state = useFromSearch();
  return <div className="grid gap-4 sm:grid-cols-2">{items.map((q, i) => <QuestionCard key={q.id} q={q} index={i} terms={terms} linkState={state} />)}</div>;
}

export function PeopleList({ items }) {
  const state = useFromSearch();
  return (
    <ul className="grid gap-3 sm:grid-cols-2" data-testid="search-people-list">
      {items.map((p) => (
        <li key={p.name}>
          <Link to={`/explore/people/${encodeURIComponent(p.name)}`} state={state} data-testid={`search-person-${p.name}`} className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:border-gold hover:shadow-md">
            <span className="font-serif text-xl font-semibold text-foreground">{p.name}</span>
            <span className="mt-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{p.asked ? `asked ${p.asked}` : ""}{p.asked && p.received ? " · " : ""}{p.received ? `was asked ${p.received}` : ""}</span>
            {p.sample && <span className="mt-3 line-clamp-2 text-sm italic text-muted-foreground">“{p.sample.question_kjv}” <span className="not-italic text-gold">{p.sample.reference}</span></span>}
          </Link>
        </li>
      ))}
    </ul>
  );
}

const FIELD_LABEL = { purpose: "Why It Was Asked", response: "The Response", historical_context: "Historical Context", commentary: "The Bigger Picture", application: "For Your Life Today" };

function NoteItems({ items, terms, state }) {
  return items.map((q) => (
        <li key={q.id}>
          <Link to={`/question/${q.id}?sec=${q.field}&hl=${encodeURIComponent(terms.join(" "))}`} state={state} data-testid={`search-note-${q.id}`} className="block rounded-2xl border border-border bg-card p-5 transition-all hover:border-gold hover:shadow-md">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"><span className="text-gold">{q.reference}</span> · found in <span className="text-foreground">{FIELD_LABEL[q.field] || q.field}</span> →</p>
            <p className="mt-1 font-serif text-lg leading-snug text-foreground">{q.question_kjv}</p>
            <p className="mt-2 text-sm text-muted-foreground"><Highlight text={q.snippet} terms={terms} /></p>
          </Link>
        </li>
  ));
}

export function NotesList({ items, life = [], terms, applicationTotal = 0 }) {
  const state = useFromSearch();
  const [open, setOpen] = useState(false);
  const main = items;
  const lifeCount = Math.max(applicationTotal, life.length);
  return (
    <div data-testid="search-notes">
      {main.length > 0 && <ul className="space-y-3" data-testid="search-notes-list"><NoteItems items={main} terms={terms} state={state} /></ul>}
      {lifeCount > 0 && (
        <div className={`${main.length ? "mt-4" : ""} rounded-2xl border border-dashed border-border`} data-testid="search-notes-application">
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="search-notes-application-toggle" className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left">
            <span className="text-sm text-foreground">Also found in <span className="font-semibold">For Your Life Today</span> <span className="font-mono text-xs text-muted-foreground">{lifeCount}</span></span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (life.length > 0
            ? <ul className="space-y-3 px-3 pb-3" data-testid="search-notes-application-list"><NoteItems items={life} terms={terms} state={state} /></ul>
            : null)}
        </div>
      )}
    </div>
  );
}

export function CommentaryList({ items, terms }) {
  const state = useFromSearch();
  return (
    <ul className="space-y-3" data-testid="search-commentary-list">
      {items.map((s) => (
        <li key={`${s.commentary}-${s.id}`}>
          <Link to={s.href} state={state} data-testid={`search-commentary-${s.commentary}-${s.id}`} className="block rounded-2xl border border-border bg-card p-5 transition-all hover:border-gold hover:shadow-md">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"><span className="text-gold">{COMMENTARY_NAMES[s.commentary] || s.commentary}</span> · {s.reference}</p>
            {s.title && <p className="mt-1 font-serif text-lg leading-snug text-foreground">{s.title}</p>}
            <p className="mt-2 text-sm text-muted-foreground"><Highlight text={s.snippet} terms={terms} /></p>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function BibleList({ items, terms }) {
  const state = useFromSearch();
  return (
    <ul className="grid gap-3 sm:grid-cols-2" data-testid="search-bible-list">
      {items.map((v) => (
        <li key={v.reference}>
          <Link to={v.href} state={state} data-testid={`search-verse-${v.book}-${v.chapter}-${v.verse}`} className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition-all hover:border-gold hover:shadow-md">
            <span className="font-mono text-[11px] uppercase tracking-wider text-gold">{v.reference} · KJV</span>
            <span className="mt-2 font-serif text-base leading-relaxed text-foreground"><Highlight text={v.text} terms={terms} /></span>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">Open in Bible Reader <ArrowRight className="h-3 w-3" /></span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function WordCard({ word }) {
  const state = useFromSearch();
  return (
    <section className="rounded-2xl border border-border bg-card p-6" data-testid="search-word-card">
      <p className="font-mono text-[11px] uppercase tracking-wider text-gold"><Languages className="mr-1 inline h-3.5 w-3.5" /> Word study · Strong's {word.strongs}</p>
      {word.count ? (
        <>
          <h2 className="mt-2 font-serif text-3xl text-foreground">{word.original} <span className="text-xl italic text-muted-foreground">{word.transliteration}</span></h2>
          <p className="mt-1 text-sm text-foreground">{word.gloss}{word.language ? ` · ${word.language}` : ""}</p>
          {word.definition && <p className="mt-2 text-sm text-muted-foreground">{word.definition}</p>}
          <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Appears in {word.count} recorded question{word.count === 1 ? "" : "s"}</p>
          <ul className="mt-2 space-y-1">
            {word.questions.map((q) => (
              <li key={q.id}><Link to={`/question/${q.id}`} state={state} className="text-sm text-primary hover:underline" data-testid={`search-word-q-${q.id}`}>{q.reference} — “{q.word}” in “{q.question_kjv}”</Link></li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">No recorded question uses Strong's {word.strongs}. Try the word in English, or open the <Link to="/read" className="text-primary hover:underline">Bible Reader</Link> and tap any word.</p>
      )}
    </section>
  );
}

export function OfferButtons({ offers }) {
  const ICON = { read: BookOpen, chapter: MessageCircleQuestion, theme: Compass, book: Compass };
  const state = useFromSearch();
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid="search-offers">
      {offers.map((o, i) => {
        const Icon = ICON[o.kind] || ArrowRight;
        return (
          <Link key={o.kind} to={o.href} state={state} data-testid={`search-offer-${o.kind}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${i === 0 ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border text-foreground hover:border-gold"}`}>
            <Icon className="h-4 w-4" /> {o.label}
          </Link>
        );
      })}
    </div>
  );
}
